import { Button, PageHeader, Space, message } from 'antd'
import { Form, Formik } from 'formik'
import get from 'lodash.get'
import set from 'lodash.set'
import moment from 'moment'
import React, { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { formOptions } from '../../forms/blocks'
import {
  get3Dlook,
  getClientDetails,
  updateClient,
} from '../../network/Clients'
import {
  createOrder as createOrderRequest,
  getOrdersMine,
} from '../../network/Orders'
import Error from '../../components/Error'
import Loader from '../../components/Loader'
import { useAuth } from '../../hooks/useAuth'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'
import { DefaultComplexion } from '../../forms/Clients'
import { getFromKey, getFixedDesignCategory } from './utils/orderHelpers'
import Step1 from './steps/Step1'
import Step2 from './steps/Step2'
import Step3 from './steps/Step3'
import Step4 from './steps/Step4'
import Step5 from './steps/Step5'

const CreateOrder = ({ history }) => {
  const { user } = useAuth()
  const { userId, storeId } = useParams()
  const myRef = useRef(null)
  const [step, setStep] = useState(1)
  const [innerStep, setInnerStep] = useState(0)
  const [afterStep3, setAfterStep3] = useState(false)
  const queryClient = useQueryClient()
  const [haveOldValue, setHaveOldValue] = useState(false)

  // ─── Data: client, 3D look, orders ─────────────────────────────────────
  const clientDetails = useQuery(
    [
      'getClientDetails',
      {
        id: userId,
      },
    ],
    getClientDetails,
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  )

  const client3DLDetails = useQuery(
    [
      '3DLook',
      {
        id: userId,
      },
    ],
    () => (process.env.NODE_ENV === 'production' ? get3Dlook(userId) : {}),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  )

  const clientOrders = useQuery(
    [
      'getOrdersMine',
      {
        sort: ['createdAt', 'DESC'],
        filter: {
          customer: userId,
        },
      },
    ],
    getOrdersMine,
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  )

  const createOrder = useMutation(createOrderRequest, {
    onSuccess: () => history.push('/orders'),
    onError: (error) => {
      // console.log('err', error.response)
      message.error(
        error.response?.data?.debugLog?.message ||
          error.response?.data?.message ||
          'An error occurs'
      )
    },
  })

  const saveUser = useMutation((val) => updateClient(val, userId), {})

  const handleBack = () => {
    if (step === 1) {
      const url = createUrlWithFiltersFromCurrentURL('/orders/create/select')
      history.push(url)
    } else {
      myRef.current?.scrollIntoView()
      setStep(step - 1)
      // if (step === 4) setAfterStep3(false)
    }
  }

  const handleNext = () => {
    if (step === 5) {
      history.push('/orders')
    } else {
      myRef.current?.scrollIntoView()
      setStep(step + 1)
      // if (step === 3) setAfterStep3(true)
    }
  }

  // ─── Handlers: navigation, save, submit ──────────────────────────────────
  const saveUserInfos = async (values) => {
    let clientInfos = get(clientDetails, 'data', {})

    let bodyMeasurements = undefined
    if (get(values, 'bodyMeasures.measurementSource', '') === 'af') {
      bodyMeasurements = {
        ...get(clientInfos, 'bodyMeasurements', {}),
        ...get(values, 'bodyMeasures', {}),
      }
    }

    // add foot measurements data to client profile
    let footMeasurements = {
      ...get(clientInfos, 'foot', {}),
      ...get(values, 'foot', {}),
    }
    values.orderProducts.forEach((product) => {
      if (
        product.orderProductType === 'custom' &&
        product.orderCustom.type === 'shoes'
      ) {
        const skuMeasurementShoes =
          product.skuMeasurements[0].skuMeasurementShoes
        if (skuMeasurementShoes) {
          footMeasurements = {
            footMeasure: skuMeasurementShoes.footMeasure,
            strongFoot: skuMeasurementShoes.strongFoot,
            picturesLeft: skuMeasurementShoes.picturesLeft,
            picturesRight: skuMeasurementShoes.picturesRight,
          }
        }
      }
    })

    // Get clientProfiles from existing client data
    const clientProfiles = get(clientInfos, 'clientProfiles', [])

    const newInfos = {
      ...clientInfos,
      complexion: {
        ...get(clientInfos, 'complexion', {}),
        ...get(values, 'complexion', {}),
      },
      bodyPictures: get(values, 'bodyPictures', []),
      bodyMeasurements,
      foot: footMeasurements,
      clientProfiles: clientProfiles.map((profile) => profile.id),
      clientTags: get(values, 'clientTags', []),

      birthDate: get(values, 'birthDate', undefined),
      country: get(values, 'country', undefined),
      city: get(values, 'city', undefined),
      nationality: get(values, 'nationality', undefined),
    }

    saveUser.mutate(newInfos)
  }

  const saveBeforeSwitch = (values) => {
    let clientInfos = get(clientDetails, 'data', {})

    // Get clientProfiles from existing client data
    const clientProfiles = get(clientInfos, 'clientProfiles', [])

    const newInfos = {
      ...clientInfos,
      complexion: {
        ...get(clientInfos, 'complexion', {}),
        ...get(values, 'complexion', {}),
      },
      bodyPictures: get(values, 'bodyPictures', []),
      bodyMeasurements: get(values, 'bodyMeasures', {}),
      foot: { ...get(clientInfos, 'foot', {}), ...get(values, 'foot', {}) },
      clientProfiles: clientProfiles,
      clientTags: get(values, 'clientTags', []),

      birthDate: get(values, 'birthDate', undefined),
      country: get(values, 'country', undefined),
      city: get(values, 'city', undefined),
      nationality: get(values, 'nationality', undefined),
    }

    saveUser.mutate(newInfos)

    queryClient.setQueryData(
      [
        'getClientDetails',
        {
          id: userId,
        },
      ],
      (oldData) => ({ ...oldData, ...newInfos })
    )
  }

  // ─── Render: step wizard ─────────────────────────────────────────────────
  return (
    <div className="" ref={myRef}>
      <PageHeader
        className="site-page-header"
        title="Create order"
        onBack={() => {
          const url = createUrlWithFiltersFromCurrentURL(
            '/orders/create/select'
          )
          history.push(url)
        }}
        style={{ backgroundColor: '#fff' }}
      />
      {clientDetails.isError ? (
        <Error retry={() => clientDetails.refetch()} />
      ) : clientDetails.isLoading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={{
            store: storeId,
            channel: 'store',
            currency: '',
            customer: userId,
            address: get(clientDetails, 'data.addresses.0', {}),
            // personaeType: 'special_event',
            bodyMeasures: {
              height: 170,
              weight: 89,
              measurementSource: 'af',
              ...get(clientDetails, 'data.bodyMeasurements', {}),
            },
            bodyPictures: get(clientDetails, 'data.bodyPictures', []),
            complexion: {
              ...DefaultComplexion,
              ...get(clientDetails, 'data.complexion', DefaultComplexion),
            },
            foot: { ...get(clientDetails, 'data.foot', {}) },
            orderProducts: [],
            discount: 0,

            birthDate: clientDetails?.data?.birthDate
              ? moment(clientDetails?.data?.birthDate)
              : undefined,
            country: get(clientDetails, 'data.country', undefined),
            city: get(clientDetails, 'data.city', undefined),
            nationality: get(clientDetails, 'data.nationality', undefined),

            comment: '',
            validated: false,
          }}
          validate={(values) => {
            const errors = {}
            if (step === 1) {
              if (!values.orderProducts.length) {
                errors.orderProducts = 'At least a product is required'
              } else {
                for (let i = 0; i < values.orderProducts.length; i++) {
                  const path = ['orderProducts', i]
                  const currentProduct = values.orderProducts[i]
                  if (
                    currentProduct.orderProductType === 'fixed_design'
                  ) {
                    // Validate fixed design product price
                    if (!get(currentProduct, `orderFixedDesign.price`, null)) {
                      set(
                        errors,
                        `orderProducts.${i}.orderFixedDesign.price`,
                        'Required'
                      )
                    }
                  } else if (
                    currentProduct.orderProductType !== 'collection' &&
                    currentProduct.orderProductType !== 'adjustment'
                  ) {
                    if (!get(currentProduct, `orderCustom.price`, null)) {
                      set(
                        errors,
                        `orderProducts.${i}.orderCustom.price`,
                        'Required'
                      )
                    }

                    formOptions[getFromKey(currentProduct)]?.forms.forEach(
                      (form) =>
                        form.form.forEach((prodForm) =>
                          prodForm.sections.forEach((section) => {
                            if (section.canAdd) {
                              section.fields.forEach((field) => {
                                const type = getFromKey(currentProduct)
                                if (type === 'shirt') {
                                  const extraShirts =
                                    currentProduct.orderCustom.orderCustomShirt
                                      .extraShirts
                                  if (extraShirts) {
                                    //validation
                                    extraShirts.forEach((item, index) => {
                                      const fieldValue = get(
                                        values,
                                        [
                                          ...path,
                                          form.key,
                                          ...prodForm.key,
                                          ...section.key,
                                          index,
                                          ...field.key,
                                        ].join('.'),
                                        ''
                                      )

                                      const name =
                                        [
                                          ...path,
                                          form.key,
                                          ...prodForm.key,
                                          ...section.key,
                                          index,
                                        ].join('.') + '.'
                                      const fieldIsRequired =
                                        field.isConditionalRequired
                                          ? field.isConditionalRequired(
                                              values,
                                              name
                                            )
                                          : !!field.isRequired
                                      const valueIsOk =
                                        !!fieldValue || fieldValue === 0
                                      const valueIsDisplay =
                                        !field.isHiding ||
                                        (field.isHiding &&
                                          !field.isHiding(values, name))

                                      if (
                                        fieldIsRequired &&
                                        !valueIsOk &&
                                        valueIsDisplay
                                      ) {
                                        set(
                                          errors,
                                          [
                                            ...path,
                                            form.key,
                                            ...prodForm.key,
                                            ...section.key,
                                            index,
                                            ...field.key,
                                          ].join('.'),
                                          'This field is required'
                                        )
                                      }
                                    })
                                  }
                                }
                              })
                            } else {
                              section.fields.forEach((field) => {
                                const isOldField = field.isOldField
                                if (isOldField) {
                                  setHaveOldValue(true)
                                }
                                const fieldValue = get(
                                  values,
                                  [
                                    ...path,
                                    form.key,
                                    ...prodForm.key,
                                    ...field.key,
                                  ].join('.'),
                                  ''
                                )
                                const name =
                                  [...path, form.key, ...prodForm.key].join(
                                    '.'
                                  ) + '.'
                                const fieldIsRequired =
                                  field.isConditionalRequired
                                    ? field.isConditionalRequired(values, name)
                                    : !!field.isRequired
                                const valueIsOk =
                                  !!fieldValue || fieldValue === 0
                                const valueIsDisplay =
                                  !field.isHiding ||
                                  (field.isHiding &&
                                    !field.isHiding(values, name))

                                const isNoValid = field.isNoValid
                                  ? field.isNoValid(values, name)
                                  : null

                                if (
                                  fieldIsRequired &&
                                  !valueIsOk &&
                                  valueIsDisplay
                                ) {
                                  set(
                                    errors,
                                    [
                                      ...path,
                                      form.key,
                                      ...prodForm.key,
                                      ...field.key,
                                    ].join('.'),
                                    'This field is required'
                                  )
                                } else if (isNoValid) {
                                  set(
                                    errors,
                                    [
                                      ...path,
                                      form.key,
                                      ...prodForm.key,
                                      ...field.key,
                                    ].join('.'),
                                    isNoValid
                                  )
                                }
                              })
                            }
                          })
                        )
                    )
                  } else if (currentProduct.orderProductType === 'adjustment') {
                    const adjustmentProduct = get(
                      values,
                      `orderProducts.${i}.orderAdjustment`,
                      []
                    )
                    if (!adjustmentProduct.type) {
                      set(
                        errors,
                        `orderProducts.${i}.orderAdjustment.type`,
                        'Select a product type'
                      )
                    } else {
                      formOptions[adjustmentProduct.type].forms.forEach(
                        (form) =>
                          form.form.forEach((prodForm) =>
                            prodForm.sections.forEach((section) =>
                              section.fields.forEach((field) => {
                                const fieldValue = get(
                                  values,
                                  [
                                    ...path,
                                    form.key.replace(
                                      'orderCustom.',
                                      'orderAdjustment.'
                                    ),
                                    ...prodForm.key,
                                    ...field.key,
                                  ].join('.'),
                                  ''
                                )

                                const name =
                                  [
                                    ...path,
                                    form.key.replace(
                                      'orderCustom.',
                                      'orderAdjustment.'
                                    ),
                                    ...prodForm.key,
                                  ].join('.') + '.'
                                const fieldIsRequired =
                                  field.isConditionalRequired
                                    ? field.isConditionalRequired(values, name)
                                    : !!field.isRequired
                                const valueIsOk =
                                  !!fieldValue || fieldValue === 0
                                const valueIsDisplay =
                                  !field.isHiding ||
                                  (field.isHiding &&
                                    !field.isHiding(values, name))
                                if (
                                  fieldIsRequired &&
                                  !valueIsOk &&
                                  valueIsDisplay
                                ) {
                                  set(
                                    errors,
                                    [
                                      ...path,
                                      form.key.replace(
                                        'orderCustom.',
                                        'orderAdjustment.'
                                      ),
                                      ...prodForm.key,
                                      ...field.key,
                                    ].join('.'),
                                    'This field is required'
                                  )
                                }
                              })
                            )
                          )
                      )
                    }
                  } else {
                    const collectionProduct = get(
                      values,
                      `orderProducts.${i}.orderCollection`,
                      []
                    )

                    if (!collectionProduct.product) {
                      set(
                        errors,
                        `orderProducts.${i}.orderCollection.product`,
                        'Select product'
                      )
                    } else {
                      // Require size when product has sizes (stored in form on product select)
                      const sizes = get(collectionProduct, 'sizes', [])
                      if (sizes.length > 0 && !collectionProduct.size) {
                        set(
                          errors,
                          `orderProducts.${i}.orderCollection.size`,
                          'Select size'
                        )
                      }
                    }

                    if (
                      !collectionProduct.price &&
                      collectionProduct.price !== 0
                    ) {
                      set(
                        errors,
                        `orderProducts.${i}.orderCollection.price`,
                        'The price is required'
                      )
                    }
                  }
                }
              }
            }

            if (step === 2) {
              if (!values.bodyMeasures?.height) {
                set(errors, 'bodyMeasures.height', 'Required')
              }
              if (!values.bodyMeasures?.weight) {
                set(errors, 'bodyMeasures.weight', 'Required')
              }
              if (!values.bodyMeasures?.shoulder) {
                set(errors, 'bodyMeasures.shoulder', 'Required')
              }
              if (!values.bodyMeasures?.chest) {
                set(errors, 'bodyMeasures.chest', 'Required')
              }

              if (!values.bodyMeasures?.waistJacket) {
                set(errors, 'bodyMeasures.waistJacket', 'Required')
              }

              if (!values.bodyMeasures?.stomach) {
                set(errors, 'bodyMeasures.stomach', 'Required')
              }
              if (!values.bodyMeasures?.hips) {
                set(errors, 'bodyMeasures.hips', 'Required')
              }
              if (!values.bodyMeasures?.biceps) {
                set(errors, 'bodyMeasures.biceps', 'Required')
              }
              if (!values.bodyMeasures?.forearm) {
                set(errors, 'bodyMeasures.forearm', 'Required')
              }
              if (!values.bodyMeasures?.wrist) {
                set(errors, 'bodyMeasures.wrist', 'Required')
              }
              if (!values.bodyMeasures?.armHole) {
                set(errors, 'bodyMeasures.armHole', 'Required')
              }
              if (!values.bodyMeasures?.thigh) {
                set(errors, 'bodyMeasures.thigh', 'Required')
              }
              if (!values.bodyMeasures?.knee) {
                set(errors, 'bodyMeasures.knee', 'Required')
              }
              if (!values.bodyMeasures?.calf) {
                set(errors, 'bodyMeasures.calf', 'Required')
              }
              if (!values.bodyMeasures?.neck) {
                set(errors, 'bodyMeasures.neck', 'Required')
              }

              if (!values.bodyMeasures?.waistPant) {
                set(errors, 'bodyMeasures.waistPant', 'Required')
              }
              if (!values.bodyMeasures?.sideNeckPoint) {
                set(errors, 'bodyMeasures.sideNeckPoint', 'Required')
              }

              if (!values.foot?.footMeasure && values.foot?.footMeasure !== 0) {
                set(errors, 'foot.footMeasure', 'Required')
              }
              // if (!values.foot?.strongFoot) {
              //   set(errors, 'foot.strongFoot', 'Required')
              // }

              if (!values.bodyMeasures?.ankle) {
                set(errors, 'bodyMeasures.ankle', 'Required')
              }
            }

            if (step === 3) {
              // innerStep
              const path = ['orderProducts', innerStep]
              const currentProduct = values.orderProducts[innerStep]

              if (!currentProduct) return

              if (currentProduct.orderProductType === 'collection') {
                // to do check collection errors
                const collectionType = get(
                  currentProduct,
                  'orderCollection.productType',
                  []
                )

                for (const prodKey of collectionType) {
                  get(formOptions, `${prodKey}.measuresForms`, []).forEach(
                    (form, formIndex) =>
                      form.form.forEach((prodForm, prodFormIndex) =>
                        prodForm.sections.forEach((section, sectionIndex) =>
                          section.fields.forEach((field, fieldIndex) => {
                            const name = [
                              ...path,
                              form.key,
                              ...prodForm.key,
                            ].join('.')

                            const fieldValue = get(
                              values,
                              name + '.' + field.key.join('.'),
                              ''
                            )

                            const fieldIsRequired = field.isConditionalRequired
                              ? field.isConditionalRequired(values, name + '.')
                              : !!field.isRequired

                            if (
                              fieldIsRequired &&
                              !fieldValue &&
                              fieldValue !== 0
                            ) {
                              set(
                                errors,
                                name + '.' + field.key.join('.'),
                                'This field is required'
                              )
                            }
                          })
                        )
                      )
                  )
                }
              } else if (currentProduct.orderProductType === 'adjustment') {
                // formOptions[currentProduct.type].measuresForms.forEach((form) =>
                //   form.form.forEach((prodForm) =>
                //     prodForm.sections.forEach((section) =>
                //       section.fields.forEach((field) => {
                //         const fieldValue = get(
                //           values,
                //           [
                //             ...path,
                //             form.key.replace(
                //               'orderCustom.',
                //               'orderAdjustment.'
                //             ),
                //             ...prodForm.key,
                //             ...field.key,
                //           ].join('.'),
                //           ''
                //         )
                //         if (
                //           field.isRequired &&
                //           !fieldValue &&
                //           fieldValue !== 0
                //         ) {
                //           set(
                //             errors,
                //             [
                //               ...path,
                //               form.key.replace(
                //                 'orderCustom.',
                //                 'orderAdjustment.'
                //               ),
                //               ...prodForm.key,
                //               ...field.key,
                //             ].join('.'),
                //             'This field is required'
                //           )
                //         }
                //       })
                //     )
                //   )
                // )
              } else {
                // Fixed design uses category-specific measure forms
                const resolvedMeasureProductType =
                  currentProduct.orderProductType === 'fixed_design'
                    ? getFixedDesignCategory(currentProduct)
                    : getFromKey(currentProduct)

                // TODO: refactor this in the future, this is ugly

                /// Jacket
                // "Waist" and "Chest" should have less than 18 cm difference
                const jacketWaist = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementJacketBlazer.waist`,
                  0
                )
                const jacketChest = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementJacketBlazer.chest`,
                  0
                )
                if (Math.abs(jacketWaist - jacketChest) > 18) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementJacketBlazer.waist`,
                    'Waist and chest difference should be less than 18cm'
                  )
                }

                /// Vest
                // The difference between "vest length front straight"
                // and "vest length back straight" should be less than 1cm
                const vestLengthFrontStraight = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementVest.lengthFrontStraight`,
                  0
                )
                const vestLengthBackStraight = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementVest.lengthBackStraight`,
                  0
                )
                const vestBottomShape = get(
                  currentProduct,
                  'orderCustom.orderCustomVest.vestBottomShape',
                  ''
                )
                if (
                  vestBottomShape === 'straight_bottom' &&
                  vestLengthFrontStraight &&
                  vestLengthBackStraight &&
                  Math.abs(vestLengthFrontStraight - vestLengthBackStraight) > 1
                ) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementVest.lengthFrontStraight`,
                    'Front and back length difference should be less than 1cm'
                  )
                }
                // The difference between "vest length front cross"
                // and "vest length back cross" should be less than 6cm
                const vestLengthFrontCross = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementVest.lengthFrontCross`,
                  0
                )
                const vestLengthBackCross = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementVest.lengthBackCross`,
                  0
                )
                if (
                  vestBottomShape === 'cross_arrow' &&
                  vestLengthFrontCross &&
                  vestLengthBackCross &&
                  Math.abs(vestLengthFrontCross - vestLengthBackCross) > 6
                ) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementVest.lengthFrontCross`,
                    'Front and back length difference should be less than 6cm'
                  )
                }
                // The difference between "chest" and "waist" should be larger than 12 cm
                const vestChest = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementVest.chest`,
                  0
                )
                const vestWaist = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementVest.waist`,
                  0
                )
                if (
                  vestChest &&
                  vestWaist &&
                  Math.abs(vestChest - vestWaist) > 12
                ) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementVest.chest`,
                    'Chest and waist difference cannot be larger than 12cm'
                  )
                }

                /// Shirt
                // The diff between lengthFront and lengthBack should be less than 18cm
                const shirtLengthFront = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementShirt.lengthFront`,
                  0
                )
                const shirtLengthBack = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementShirt.lengthBack`,
                  0
                )
                if (Math.abs(shirtLengthFront - shirtLengthBack) > 18) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.lengthFront`,
                    'Front and back length difference should be less than 18cm'
                  )
                }
                // Prefill "shirt length back" depending on "shirt length front"
                if (shirtLengthFront && !shirtLengthBack) {
                  set(
                    values,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.lengthBack`,
                    shirtLengthFront
                  )
                }
                // The diff between chest and waist should be less than 18cm
                const shirtChest = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementShirt.chest`,
                  0
                )
                const shirtWaist = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementShirt.waist`,
                  0
                )
                if (
                  shirtChest &&
                  shirtWaist &&
                  Math.abs(shirtChest - shirtWaist) > 18
                ) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.chest`,
                    'Chest and waist difference should be less than 18cm'
                  )
                }
                //  Prefill the "Shirt sleeve wrist (left)"
                //  depending on the field "Shirt sleeve wrist (right)" -> Can be modified
                const shirtSleeveWristRight = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementShirt.sleeveWristRight`,
                  0
                )
                const shirtSleeveWristLeft = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementShirt.sleeveWristLeft`,
                  0
                )
                if (shirtSleeveWristLeft && !shirtSleeveWristRight) {
                  set(
                    values,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.sleeveWristRight`,
                    shirtSleeveWristLeft
                  )
                }

                /// Jeans
                // The sum of "front crotch" and "back crotch" need to be equal with "whole crotch"
                const jeansFrontCrotch = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementJean.frontCrotch`,
                  0
                )
                const jeansBackCrotch = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementJean.backCrotch`,
                  0
                )
                const jeansWholeCrotch = get(
                  currentProduct,
                  `skuMeasurements.0.skuMeasurementJean.crotch`,
                  0
                )
                if (
                  jeansFrontCrotch &&
                  jeansBackCrotch &&
                  jeansWholeCrotch &&
                  jeansFrontCrotch + jeansBackCrotch !== jeansWholeCrotch
                ) {
                  set(
                    errors,
                    `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementJean.crotch`,
                    'The sum of "front crotch" and "back crotch" need to be equal with "whole crotch"'
                  )
                }

                get(
                  formOptions,
                  `${resolvedMeasureProductType}.measuresForms`,
                  []
                ).forEach(
                  // formOptions[getFromKey(currentProduct)].measuresForms.forEach(
                  (form) =>
                    form.form.forEach((prodForm) =>
                      prodForm.sections.forEach((section) =>
                        section.fields.forEach((field) => {
                          const name = [
                            ...path,
                            form.key,
                            ...prodForm.key,
                          ].join('.')

                          const fieldValue = get(
                            values,
                            name + '.' + field.key.join('.'),
                            ''
                          )

                          const fieldIsRequired = field.isConditionalRequired
                            ? field.isConditionalRequired(values, name + '.')
                            : !!field.isRequired

                          const fieldIsDisplay =
                            !field.isHiding ||
                            (field.isHiding && !field.isHiding(values, name))

                          if (
                            fieldIsRequired &&
                            !fieldValue &&
                            fieldValue !== 0 &&
                            fieldIsDisplay
                          ) {
                            set(
                              errors,
                              name + '.' + field.key.join('.'),
                              'This field is required'
                            )
                          }
                        })
                      )
                    )
                )
              }
            }
            return errors
          }}
          onSubmit={async (values) => {
            let paymentStatus = 'unpaid'

            if (values.balance === 0) {
              paymentStatus = 'paid'
            } else if (values.deposit || values.deposit2) {
              paymentStatus = 'deposit'
            }

            const orderProducts = values.orderProducts.map((prod) => {
              // Normalize collection product payload
              const normalized =
                prod.orderProductType === 'collection'
                  ? (() => {
                      const {
                        options,
                        optionsTemp,
                        optionsFreeText,
                        sizes,
                        ...rest
                      } = prod.orderCollection ?? {}
                      return {
                        ...prod,
                        orderCollection: {
                          ...rest,
                          size: prod.orderCollection?.size ?? undefined,
                        },
                      }
                    })()
                  : prod

              // For custom products, enrich skuMeasurements with size preset meta (changes vs base)
              if (
                normalized.orderProductType === 'custom' &&
                Array.isArray(normalized.skuMeasurements)
              ) {
                normalized.skuMeasurements = normalized.skuMeasurements.map(
                  (skuMeas) => {
                    if (!skuMeas || typeof skuMeas !== 'object') return skuMeas
                    const keys = Object.keys(skuMeas)
                    if (keys.length !== 1) return skuMeas

                    const measKey = keys[0] // e.g. 'skuMeasurementSkirt'
                    const meas = skuMeas[measKey]
                    if (!meas || typeof meas !== 'object') return skuMeas

                    const meta = meas.__sizePresetMeta
                    if (!meta || !meta.base) return skuMeas

                    const base = meta.base || {}
                    let hasChanges = false

                    Object.keys(base).forEach((fieldKey) => {
                      const baseVal = base[fieldKey]
                      const currVal = meas[fieldKey]

                      if (typeof baseVal === 'number') {
                        const currNum =
                          typeof currVal === 'number'
                            ? currVal
                            : currVal === null ||
                              currVal === undefined ||
                              currVal === ''
                            ? baseVal
                            : Number(currVal)
                        if (!Number.isNaN(currNum) && currNum !== baseVal) {
                          hasChanges = true
                        }
                      } else if (typeof baseVal === 'string') {
                        if (
                          currVal !== undefined &&
                          currVal !== null &&
                          currVal !== '' &&
                          currVal !== baseVal
                        ) {
                          hasChanges = true
                        }
                      }
                    })

                    return {
                      ...skuMeas,
                      [measKey]: {
                        ...meas,
                        __sizePresetMeta: {
                          ...meta,
                          hasChanges,
                        },
                      },
                    }
                  }
                )
              }

              return normalized
            })

            /* not used code but overwirte useful data
            saveUserInfos({
              detailedAddress: get(values, 'detailedAddress', ''),
            })
            */
            createOrder.mutate({
              ...values,
              orderProducts,
              orderType: 'classic_order',
              paymentStatus,
              ...(user?.id != null ? { createdBy: user.id } : {}),
            })
          }}
        >
          {({ values, errors, handleChange, validateForm }) => (
            <Form style={{ padding: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {step === 1 && (
                  <Step1
                    values={values}
                    errors={errors}
                    validateForm={validateForm}
                    handleChange={handleChange}
                    handleBack={handleBack}
                    handleNext={handleNext}
                  />
                )}
                {step === 2 && (
                  <Step2
                    values={values}
                    errors={errors}
                    validateForm={validateForm}
                    handleChange={handleChange}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    saveUserInfos={() => saveUserInfos(values)}
                    saveBeforeSwitch={() => saveBeforeSwitch(values)}
                    clientDetails={{
                      af: clientDetails.data,
                      dlook: client3DLDetails.data,
                    }}
                  />
                )}
                {step === 3 && (
                  <Step3
                    values={values}
                    errors={errors}
                    validateForm={validateForm}
                    innerStep={innerStep}
                    setInnerStep={setInnerStep}
                    afterStep3={afterStep3}
                    setAfterStep3={setAfterStep3}
                    handleChange={handleChange}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    clientOrders={clientOrders}
                  />
                )}
                {step === 4 && (
                  <Step4
                    values={values}
                    errors={errors}
                    // validateForm={validateForm}
                    handleChange={handleChange}
                  />
                )}
                {step === 5 && (
                  <Step5
                    values={values}
                    errors={errors}
                    // validateForm={validateForm}
                    handleChange={handleChange}
                  />
                )}
                {step !== 1 && step !== 2 && step !== 3 && (
                  <Space>
                    <Button
                      disabled={createOrder.isLoading}
                      onClick={handleBack}
                      type="secondary"
                    >
                      Previous
                    </Button>
                    {step !== 5 && (
                      <Button
                        disabled={Object.keys(errors).length}
                        onClick={() => {
                          if (step === 4) saveUserInfos(values)
                          handleNext()
                        }}
                        type="primary" /*htmlType="submit"*/
                      >
                        {step === 4 ? 'Payment' : 'Next'}
                      </Button>
                    )}
                    {step === 5 && (
                      <Button
                        loading={createOrder.isLoading}
                        disabled={Object.keys(errors).length}
                        // onClick={handleNext}
                        type="primary"
                        htmlType="submit"
                      >
                        Finish order
                      </Button>
                    )}
                  </Space>
                )}
              </Space>
            </Form>
          )}
        </Formik>
      )}
    </div>
  )
}

export default CreateOrder